// Help: quick fixes for what usually goes wrong, and "Tell VERA": the person writes once (a screenshot if it helps),
// VERA reads it and shows a card (what she understood, her steps, what changes, and for the team's work the exact
// summary the VERANeural team would get). Nothing happens until they approve. Workspace changes go to VERA Build; the
// team's work reaches support@veraneural.com as the approved summary only. Everyone can use it, from Build or from
// the menu under their name. If VERA can't read it, the person can still send it to support as written.

import { useRef, useState } from "react";
import { cpFetch } from "../lib/controlPlane";
import { readFiles } from "../lib/attachments";
import { forTheTeam, requests, type StudioRequest } from "../lib/requests";
import { vera, type Attachment } from "../api/vera";
import { Icon, useStudio } from "./ui";
import { currentLang, T } from "../lib/i18n";

const FIXES: { q: string; a: string }[] = [
  { q: "VERA doesn't answer, or says she's unavailable", a: "Check your signal, then close Studio and open it again. If it keeps happening, sign out and back in from your name at the top." },
  { q: "The phone app asks me to sign in again", a: "Open Studio from the icon on your home screen and sign in once. It remembers you after that." },
  { q: "Talk or Dictate doesn't hear me", a: "Turn on the microphone in Privacy. On iPhone also open Settings, then Safari, then Microphone, and choose Allow." },
  { q: "A photo or note hasn't arrived", a: "It's saved on your phone and goes on its own when you have signal. Until then it shows as waiting at the bottom." },
  { q: "VERA on WhatsApp doesn't reply", a: "Write from the phone number VERA knows you by. If there's still no answer after a minute, send it below as a problem." },
  { q: "My email or calendar isn't showing", a: "Open Connections and connect Microsoft 365 or Google again. It takes a minute." },
  { q: "Something changed that I didn't want", a: "Every change is in Activity, and changes made in Build can be undone there or here." },
];

const KINDS = [
  { id: "problem", label: "Something's wrong", icon: "x" },
  { id: "question", label: "A question", icon: "chat" },
  { id: "idea", label: "An idea", icon: "sparkle" },
] as const;

/** What VERA is asked when she reads a screenshot for a request. */
const READ_SHOT = "Describe this screenshot for a support request: which screen it is, what it shows, any error message word for word, and anything that looks wrong. Plain sentences, no commentary.";

/** `onBuild`: hand an approved workspace change to VERA Build (absent where Build isn't open to this person). */
export function HelpPane({ onBuild }: { onBuild?: (request: string) => void }) {
  const { getToken, profile, user, toast } = useStudio();
  const [kind, setKind] = useState<(typeof KINDS)[number]["id"]>("problem");
  const [message, setMessage] = useState("");
  const [replyTo, setReplyTo] = useState(user.email && user.email.includes("@") ? user.email : "");
  const [shots, setShots] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [req, setReq] = useState<StudioRequest | null>(null);
  const [outcome, setOutcome] = useState<"" | "sent" | "build" | "closed" | "support">("");
  const [changing, setChanging] = useState(false);
  const [change, setChange] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const page = () => `${profile.key} ${location.pathname}${location.hash}`.trim();

  async function addShots(list: FileList | null) {
    if (!list?.length) return;
    const r = await readFiles(Array.from(list).filter((f) => f.type.startsWith("image/")), shots.length);
    if (r.files.length) setShots((s) => [...s, ...r.files].slice(0, 3));
    if (r.skipped.length) toast(`${T("Not added:")} ${r.skipped.join("; ")}`);
  }

  async function send() {
    if ((message.trim().length < 3 && !shots.length) || busy) return;
    setError(""); setOutcome("");
    try {
      const read: { name: string; text: string }[] = [];
      for (const s of shots) {
        setBusy(T("VERA is looking at your screenshot…"));
        try { read.push({ name: s.name, text: (await vera.ask({ text: READ_SHOT, scope: profile.scopes[0] ?? "marine", attachments: [s] }, getToken)).text }); }
        catch { /* the words still go; the screenshot is named */ read.push({ name: s.name, text: T("(VERA couldn't read this screenshot.)") }); }
      }
      setBusy(T("VERA is reading it…"));
      const r = await requests.create(getToken, { source: "help", kind, message: message.trim(), page: page(), device: navigator.userAgent, replyTo: replyTo.trim() || undefined, attachments: read, language: currentLang() });
      // Requests aren't on for this workspace yet: it goes to support as written, as Help always did.
      if (r.status === "off") { setBusy(""); await sendAsWritten(); return; }
      if (r.request) setReq(r.request); else setError(r.error ?? T("VERA couldn't read it just now."));
    } catch (e) {
      setError(e instanceof Error ? e.message : T("VERA couldn't read it just now."));
    } finally { setBusy(""); }
  }

  /** When VERA can't read it (or requests aren't on yet): send it to support exactly as written, on purpose. */
  async function sendAsWritten() {
    setBusy(T("Sending…")); setError("");
    try {
      await cpFetch(getToken, "/v1/support", { method: "POST", body: JSON.stringify({ kind, message: message.trim() || shots.map((s) => s.name).join(", "), replyTo: replyTo.trim() || undefined, page: page(), device: navigator.userAgent }) });
      setOutcome("support"); setReq(null); setMessage(""); setShots([]);
    } catch (e) { setError(e instanceof Error ? e.message : T("It didn't go through. Try again in a minute.")); }
    finally { setBusy(""); }
  }

  async function decide(decision: "approve" | "change" | "notnow") {
    if (!req) return;
    setBusy(decision === "change" ? T("VERA is reading it again…") : T("One moment…")); setError("");
    try {
      const r = await requests.decide(getToken, req.id, decision, decision === "change" ? change.trim() : undefined);
      if (r.request) setReq(r.request);
      if (decision === "change") { setChanging(false); setChange(""); return; }
      if (r.status === "build") {
        if (onBuild) { onBuild(r.request?.card?.understood ?? message); setOutcome("build"); }
        else setOutcome("build");
      } else if (r.status === "sent") setOutcome("sent");
      else if (r.status === "closed") setOutcome("closed");
      if (r.error) setError(r.error);
    } catch (e) { setError(e instanceof Error ? e.message : T("That didn't go through. Nothing changed.")); }
    finally { setBusy(""); }
  }

  function again() { setReq(null); setOutcome(""); setMessage(""); setShots([]); setError(""); setChanging(false); }

  const card = req?.card;

  return (
    <div className="stack help-pane" style={{ gap: 14 }}>
      <div className="stack" style={{ gap: 6 }}>
        <h3>{T("Quick fixes")}</h3>
        {FIXES.map((f) => (
          <details key={f.q} className="help-fix">
            <summary>{T(f.q)}</summary>
            <p className="mute">{T(f.a)}</p>
          </details>
        ))}
      </div>

      <div className="stack cp-card" style={{ gap: 10 }} aria-live="polite">
        <h3>{T("Tell VERA")}</h3>

        {outcome === "sent" && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="notice calm" role="status"><Icon name="check" size={15} /><span>{T("Sent to the VERANeural team, as you approved it. VERA follows it and tells you what happens.")}</span></div>
            <button className="btn" onClick={again}>{T("Ask something else")}</button>
          </div>
        )}
        {outcome === "build" && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="notice calm" role="status"><Icon name="sparkle" size={15} /><span>{onBuild ? T("VERA Build is planning it now. You'll see the change before anything happens.") : T("This change is made in VERA Build by a workspace owner. VERA has kept your request for them.")}</span></div>
            <button className="btn" onClick={again}>{T("Ask something else")}</button>
          </div>
        )}
        {outcome === "closed" && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="notice calm" role="status"><Icon name="check" size={15} /><span>{T("All right. Nothing was done, and it's kept if you need it later.")}</span></div>
            <button className="btn" onClick={again}>{T("Ask something else")}</button>
          </div>
        )}
        {outcome === "support" && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="notice calm" role="status"><Icon name="check" size={15} /><span>{T("Sent. The VERANeural team has it and will get back to you.")}</span></div>
            <button className="btn" onClick={again}>{T("Ask something else")}</button>
          </div>
        )}

        {!outcome && req && card && (
          <div className="stack" style={{ gap: 10 }}>
            <div className="stack" style={{ gap: 4 }}>
              <b>{T("What I understood")}</b>
              <p style={{ margin: 0 }}>{card.understood}</p>
            </div>
            {card.answer && <div className="stack" style={{ gap: 4 }}><b>{T("The answer")}</b><p style={{ margin: 0 }}>{card.answer}</p></div>}
            {card.steps.length > 0 && (
              <div className="stack" style={{ gap: 4 }}>
                <b>{T("What I'll do")}</b>
                <ol style={{ margin: 0, paddingLeft: 20 }}>{card.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              </div>
            )}
            {card.changesForYou && <div className="stack" style={{ gap: 4 }}><b>{T("What changes for you")}</b><p style={{ margin: 0 }}>{card.changesForYou}</p></div>}
            {forTheTeam(card) && card.forHq && (
              <div className="stack" style={{ gap: 4 }}>
                <b>{T("What goes to the VERANeural team")}</b>
                <p className="mute" style={{ margin: 0 }}>{card.forHq}</p>
                <small className="mute">{T("Only this summary. Your own words, screenshots and data stay in your workspace.")}</small>
              </div>
            )}
            {card.question && <div className="notice calm"><span>{card.question}</span></div>}
            {changing ? (
              <div className="stack" style={{ gap: 6 }}>
                <textarea value={change} onChange={(e) => setChange(e.target.value)} rows={3} aria-label={T("What should be different?")} placeholder={T("What should be different?")} />
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn primary" disabled={change.trim().length < 2 || Boolean(busy)} onClick={() => void decide("change")}>{T("Read it again")}</button>
                  <button className="btn" onClick={() => setChanging(false)}>{T("Back")}</button>
                </div>
              </div>
            ) : (
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                {card.type === "question"
                  ? <button className="btn primary" disabled={Boolean(busy)} onClick={() => void decide("approve")}><Icon name="check" size={15} />{T("That helps")}</button>
                  : <button className="btn primary" disabled={Boolean(busy)} onClick={() => void decide("approve")}><Icon name="check" size={15} />{T("Approve")}</button>}
                <button className="btn" disabled={Boolean(busy)} onClick={() => setChanging(true)}>{T("Change something")}</button>
                <button className="btn ghost" disabled={Boolean(busy)} onClick={() => void decide("notnow")}>{T("Not now")}</button>
              </div>
            )}
          </div>
        )}

        {!outcome && req && !card && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="notice calm" role="status"><span>{T("VERA couldn't read it just now. You can send it to the VERANeural team exactly as you wrote it, or try again in a minute.")}</span></div>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn primary" disabled={Boolean(busy)} onClick={() => void sendAsWritten()}><Icon name="send" size={15} />{T("Send as written")}</button>
              <button className="btn" onClick={() => setReq(null)}>{T("Back")}</button>
            </div>
          </div>
        )}

        {!outcome && !req && (
          <>
            <p className="mute">{T("Write it once. VERA reads it, tells you what she understood and what she'll do, and nothing happens until you say yes.")}</p>
            <div className="row" role="radiogroup" aria-label={T("What is it?")} style={{ gap: 6, flexWrap: "wrap" }}>
              {KINDS.map((k) => (
                <button key={k.id} role="radio" aria-checked={kind === k.id} className={`btn${kind === k.id ? " primary" : ""}`} onClick={() => setKind(k.id)}>
                  <Icon name={k.icon} size={14} />{T(k.label)}
                </button>
              ))}
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} aria-label={T("What happened, or what you need")}
              placeholder={T(kind === "problem" ? "What happened, and what you expected instead" : kind === "idea" ? "What would make Studio better for you" : "What you'd like to know")} />
            <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn" onClick={() => fileRef.current?.click()} disabled={shots.length >= 3}><Icon name="img" size={14} />{T("Add a screenshot")}</button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { void addShots(e.target.files); e.target.value = ""; }} />
              {shots.map((s, i) => (
                <span key={i} className="cp-chip">{s.thumb ? <img src={s.thumb} alt="" className="cp-thumb" style={{ width: 20, height: 20 }} /> : <Icon name="img" size={13} />}{s.name}
                  <button className="iconbtn ghost" aria-label={T("Remove")} onClick={() => setShots((xs) => xs.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button></span>
              ))}
            </div>
            <input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} autoComplete="email"
              placeholder={T("Your email for the answer")} aria-label={T("Your email for the answer")} />
            <button className="btn primary" disabled={(message.trim().length < 3 && !shots.length) || Boolean(busy)} onClick={() => void send()}>
              <Icon name="send" size={15} />{T("Send to VERA")}
            </button>
          </>
        )}
        {busy && <span className="cp-line" role="status">{busy}</span>}
        {error && (
          <div className="stack" style={{ gap: 6 }}>
            <span className="cp-line warn" role="alert">{error}</span>
            {!req && !outcome && (message.trim().length >= 3 || shots.length > 0) && <button className="btn" onClick={() => void sendAsWritten()}>{T("Send to support as written")}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

/** Help as its own panel, for everyone, from the menu under their name. */
export function HelpDock({ onClose, onBuild }: { onClose: () => void; onBuild?: (request: string) => void }) {
  return (
    <aside className="codepanel buildpanel" aria-label={T("Help")}>
      <div className="cp-head">
        <Icon name="chat" size={17} /><b>{T("Help")}</b>
        <button className="iconbtn ghost" style={{ marginLeft: "auto" }} onClick={onClose} aria-label={T("Close Help")} title={T("Close")}><Icon name="x" size={16} /></button>
      </div>
      <div className="cp-conv"><HelpPane onBuild={onBuild} /></div>
    </aside>
  );
}
